/*
 * GAIT cart helpers (commerce, DESIGN.md sections 4 and 5). Vanilla ES2018, no dependencies, no build step.
 *
 * A device line carries the private property _gx_group; its protection plan (AppleCare+) carries
 * _gx_parent with the same value. A line whose product type is a plan type (PLAN_TYPES) is a plan even
 * without _gx_parent (added on its own, e.g. from the plan's product URL or a crafted cart/add request);
 * it has no device, so it is an orphan. This script keeps every plan quantity equal to its device quantity
 * and removes orphan plans (device gone or never there):
 *   - cart page ([data-gx-cart-page]): quantity/remove links ([data-gx-cart-change]) change the device and
 *     its plans together, then the page reloads; on load a pending sync ([data-gx-cart-sync="needed"],
 *     computed in Liquid) is applied once;
 *   - mini-cart: after every theme:cart:updated (jQuery event fired by the theme and the configurator).
 * All requests go to Shopify.routes.root + 'cart/change.js' with line keys, so they work under /ar/.
 * The page only reloads after a sync when at least one change was applied, and never twice within
 * RELOAD_WINDOW_MS (sessionStorage, plus history.state when storage is blocked).
 * Pure helpers are exposed on window.GxCart for unit tests.
 */
(function () {
  'use strict';

  var root = typeof window !== 'undefined' ? window : null;
  if (!root || (root.GxCart && root.GxCart.__ready)) return;

  var GROUP = '_gx_group';
  var PARENT = '_gx_parent';
  // Product types (lower case) of protection-plan products; keep in sync with gx_plan_types in
  // sections/cart-template.liquid and snippets/header-mini-cart.liquid (and with the plan kinds in
  // sections/product-configurator.liquid: AppleCare+, GaitCare).
  var PLAN_TYPES = ['applecare+', 'gaitcare'];
  var RELOAD_KEY = 'gx-cart-sync-reload';
  var RELOAD_WINDOW_MS = 15000;

  /* ---------- pure helpers ---------- */

  function propertyOf(item, name) {
    var props = item && item.properties;
    if (!props || typeof props !== 'object') return '';
    var value = props[name];
    if (value === undefined || value === null) return '';
    return String(value).trim();
  }

  function parentOf(item) {
    return propertyOf(item, PARENT);
  }

  function productTypeOf(item) {
    var type = item && item.product_type;
    return typeof type === 'string' ? type.trim().toLowerCase() : '';
  }

  /** A plan line: linked to a device (_gx_parent), or a plan product added without one. */
  function isPlanLine(item) {
    return parentOf(item) !== '' || PLAN_TYPES.indexOf(productTypeOf(item)) !== -1;
  }

  function groupOf(item) {
    return isPlanLine(item) ? '' : propertyOf(item, GROUP);
  }

  function itemsOf(cart) {
    return cart && Array.isArray(cart.items) ? cart.items : [];
  }

  function toQuantity(value) {
    var n = Math.floor(Number(value));
    return isFinite(n) && n > 0 ? n : 0;
  }

  /** First device line per group id. */
  function devicesByGroup(cart) {
    var map = {};
    itemsOf(cart).forEach(function (item) {
      var group = groupOf(item);
      if (group && !Object.prototype.hasOwnProperty.call(map, group)) map[group] = item;
    });
    return map;
  }

  /**
   * Changes needed so that every plan line has its device's quantity; orphan plans (no _gx_parent, or a
   * parent group without a device line) get quantity 0.
   * Returns [{ id: lineKey, quantity }] in cart order (removals and updates), without duplicates.
   */
  function planSyncOps(cart) {
    var devices = devicesByGroup(cart);
    var seen = {};
    var ops = [];
    itemsOf(cart).forEach(function (item) {
      if (!isPlanLine(item) || !item.key || seen[item.key]) return;
      seen[item.key] = true;
      var parent = parentOf(item);
      var device = Object.prototype.hasOwnProperty.call(devices, parent) ? devices[parent] : null;
      var wanted = device ? toQuantity(device.quantity) : 0;
      if (toQuantity(item.quantity) !== wanted) ops.push({ id: item.key, quantity: wanted });
    });
    return ops;
  }

  /**
   * Changes for "set line `key` to `quantity`": a device line takes its plans along (0 removes them too);
   * a plan line can only be removed (its quantity follows the device). Other drift is fixed as well.
   */
  function changeOps(cart, key, quantity) {
    var items = itemsOf(cart);
    var wanted = toQuantity(quantity);
    var target = null;
    for (var i = 0; i < items.length; i += 1) {
      if (items[i] && items[i].key === key) {
        target = items[i];
        break;
      }
    }
    if (!target) return [];

    var ops = [];
    var touched = {};
    var push = function (id, qty) {
      if (touched[id]) return;
      touched[id] = true;
      ops.push({ id: id, quantity: qty });
    };

    if (isPlanLine(target)) {
      if (wanted === 0) push(target.key, 0);
    } else {
      if (toQuantity(target.quantity) !== wanted) push(target.key, wanted);
      var group = groupOf(target);
      if (group) {
        items.forEach(function (item) {
          if (isPlanLine(item) && parentOf(item) === group && toQuantity(item.quantity) !== wanted) push(item.key, wanted);
        });
      }
    }

    // Any other plan drift in the same request (devices other than the target).
    var projected = {
      items: items
        .map(function (item) {
          var op = null;
          for (var j = 0; j < ops.length; j += 1) if (ops[j].id === item.key) op = ops[j];
          if (!op) return item;
          var copy = {};
          for (var k in item) if (Object.prototype.hasOwnProperty.call(item, k)) copy[k] = item[k];
          copy.quantity = op.quantity;
          return copy;
        })
        .filter(function (item) {
          return toQuantity(item.quantity) > 0;
        })
    };
    planSyncOps(projected).forEach(function (op) {
      push(op.id, op.quantity);
    });
    return ops;
  }

  /**
   * True when at least one of `ops` was applied: it is no longer pending (same line key and quantity) in
   * planSyncOps(fresh). False when every op failed or the server ignored it, so a reload would show the
   * same cart again.
   */
  function syncProgressed(ops, fresh) {
    var pending = planSyncOps(fresh);
    return (Array.isArray(ops) ? ops : []).some(function (op) {
      return !pending.some(function (left) {
        return left.id === op.id && left.quantity === op.quantity;
      });
    });
  }

  /** Locale-aware cart root, e.g. "/" or "/ar/". */
  function routesRoot() {
    var shopify = root.Shopify;
    var value = shopify && shopify.routes && shopify.routes.root;
    if (typeof value !== 'string' || value === '') value = '/';
    return value.charAt(value.length - 1) === '/' ? value : value + '/';
  }

  /* ---------- network ---------- */

  function request(path, body) {
    var options = { credentials: 'same-origin', headers: { Accept: 'application/json' } };
    if (body) {
      options.method = 'POST';
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }
    return root.fetch(routesRoot() + path, options).then(function (response) {
      if (!response || !response.ok) throw new Error('GxCart: ' + path + ' failed');
      return response.json();
    });
  }

  function fetchCart() {
    return request('cart.js');
  }

  /** Applies ops one by one (Shopify cart writes must not overlap), then resolves with the fresh cart. */
  function applyOps(ops) {
    var list = Array.isArray(ops) ? ops : [];
    var failed = 0;
    var chain = list.reduce(function (previous, op) {
      return previous.then(function () {
        return request('cart/change.js', { id: op.id, quantity: op.quantity }).catch(function () {
          failed += 1;
        });
      });
    }, Promise.resolve());
    return chain.then(function () {
      return fetchCart().then(function (cart) {
        if (cart && typeof cart === 'object') cart.gxFailedOps = failed;
        return cart;
      });
    });
  }

  /* ---------- DOM wiring ---------- */

  var state = { busy: false, notifying: false };

  function noop() {}

  function jq() {
    var $ = root.jQuery;
    return typeof $ === 'function' && $.fn ? $ : null;
  }

  function cartPage(doc) {
    return doc && doc.querySelector ? doc.querySelector('[data-gx-cart-page]') : null;
  }

  function isRecent(value) {
    var at = Number(value);
    return isFinite(at) && at > 0 && Date.now() - at < RELOAD_WINDOW_MS;
  }

  /** History entry state (survives location.reload(), independent of Web Storage); {} when unusable. */
  function historyState() {
    try {
      var entry = root.history && root.history.state;
      return entry && typeof entry === 'object' ? entry : {};
    } catch (error) {
      return {};
    }
  }

  function recentlyReloaded() {
    var stored = null;
    try {
      stored = root.sessionStorage ? root.sessionStorage.getItem(RELOAD_KEY) : null;
    } catch (error) {
      stored = null; // storage blocked: fall back to the history entry
    }
    return isRecent(stored) || isRecent(historyState()[RELOAD_KEY]);
  }

  function markReload() {
    var now = String(Date.now());
    try {
      if (root.sessionStorage) root.sessionStorage.setItem(RELOAD_KEY, now);
    } catch (error) {
      /* storage blocked (privacy settings): the history entry below still guards against a loop */
    }
    try {
      var history = root.history;
      if (!history || typeof history.replaceState !== 'function') return;
      var current = history.state;
      if (current !== null && current !== undefined && typeof current !== 'object') return; // not ours to replace
      var next = {};
      if (current) for (var k in current) if (Object.prototype.hasOwnProperty.call(current, k)) next[k] = current[k];
      next[RELOAD_KEY] = now;
      history.replaceState(next, ''); // same URL; the theme's own state keys are kept
    } catch (error) {
      /* no history API: the progress check in syncDrawer still stops repeated reloads */
    }
  }

  function reloadPage() {
    if (root.location && typeof root.location.reload === 'function') root.location.reload();
  }

  /** Tell the theme (mini-cart, header count) about the new cart; ignore our own echo. */
  function notifyTheme(cart) {
    var $ = jq();
    if (!$ || !cart) return;
    state.notifying = true;
    try {
      $(root.document).trigger('theme:cart:updated', [cart, false, true]);
    } finally {
      state.notifying = false;
    }
  }

  function rerenderMiniCart() {
    var $ = jq();
    if (!$) return;
    var plugin = $('.mini-cart').data('plugin_trademarkMiniCart');
    if (plugin && typeof plugin._rerender === 'function') plugin._rerender();
  }

  /**
   * Applies planSyncOps(cart). Resolves true when at least one change was applied, after reloading the cart
   * page or re-firing theme:cart:updated with the fresh cart. Resolves false (and does nothing else) when
   * there was nothing to do or nothing could be applied: reloading then would show the same cart and, on the
   * cart page, start the same failing sync again (an endless loop when the reload guard has no storage).
   * The cart page then keeps its Liquid notice, disabled checkout and per-plan fix links.
   */
  function syncDrawer(cart) {
    var ops = planSyncOps(cart);
    if (!ops.length) return Promise.resolve(false);
    state.busy = true;
    return applyOps(ops).then(
      function (fresh) {
        state.busy = false;
        if (!syncProgressed(ops, fresh)) return false;
        if (cartPage(root.document)) {
          markReload();
          reloadPage();
        } else {
          notifyTheme(fresh);
        }
        return true;
      },
      function () {
        state.busy = false;
        return false;
      }
    );
  }

  /** The theme changed the cart (e.g. a mini-cart removal) and no sync re-rendered it: show that change. */
  function afterThemeChange(rerender) {
    if (cartPage(root.document)) {
      // A mini-cart removal on the cart page: show the server truth.
      markReload();
      reloadPage();
      return;
    }
    // The theme only swaps the total after a removal; refresh so savings and nested plans stay correct.
    var doc = root.document;
    if (!rerender && doc && doc.querySelector && doc.querySelector('.gx-mini-cart__savings, .gx-mini-plan')) rerenderMiniCart();
  }

  function onThemeCartUpdated(event, cart, forceOpening, rerender) {
    if (state.busy || state.notifying || !cart || !Array.isArray(cart.items)) return;
    if (planSyncOps(cart).length) {
      syncDrawer(cart).then(function (synced) {
        if (!synced) afterThemeChange(rerender);
      });
      return;
    }
    afterThemeChange(rerender);
  }

  function onCartPageClick(event) {
    var page = cartPage(root.document);
    var target = event && event.target;
    var link = target && typeof target.closest === 'function' ? target.closest('[data-gx-cart-change]') : null;
    if (!page || !link || !page.contains(link)) return;
    var key = link.getAttribute('data-line-key');
    var quantity = parseInt(link.getAttribute('data-quantity'), 10);
    if (!key || isNaN(quantity) || typeof root.fetch !== 'function') return; // plain link fallback
    event.preventDefault();
    if (state.busy) return;

    state.busy = true;
    page.classList.add('gx-cart--busy');
    page.setAttribute('aria-busy', 'true');
    var fallback = link.getAttribute('href');
    // The no-JS href targets a line by its INDEX (line=N). Once key-based writes have started the cart
    // order may have changed, so following it could change a different product: only use it while
    // nothing has been written, otherwise reload to show the server state.
    var writing = false;

    fetchCart()
      .then(function (cart) {
        writing = true;
        return applyOps(changeOps(cart, key, quantity));
      })
      .then(
        function () {
          reloadPage();
        },
        function () {
          state.busy = false;
          if (writing) reloadPage();
          else if (fallback && root.location) root.location.href = fallback;
        }
      );
  }

  function syncOnLoad() {
    var doc = root.document;
    if (!doc.querySelector('[data-gx-cart-sync="needed"]')) return;
    if (cartPage(doc) && recentlyReloaded()) return; // never loop
    fetchCart().then(syncDrawer).catch(noop);
  }

  function init() {
    var doc = root.document;
    if (!doc || init.done) return;
    init.done = true;

    var $ = jq();
    if ($) {
      $(doc).on('theme:cart:updated', onThemeCartUpdated);
      // The theme mini-cart's destroy() (theme editor section reload) calls $(document).off('theme:cart:updated').
      $(doc).on('shopify:section:load', function () {
        $(doc).off('theme:cart:updated', onThemeCartUpdated).on('theme:cart:updated', onThemeCartUpdated);
      });
    }

    if (cartPage(doc)) doc.addEventListener('click', onCartPageClick);
    if (typeof root.fetch === 'function') syncOnLoad();
  }

  root.GxCart = {
    __ready: true,
    planSyncOps: planSyncOps,
    changeOps: changeOps,
    syncProgressed: syncProgressed,
    devicesByGroup: devicesByGroup,
    isPlanLine: isPlanLine,
    parentOf: parentOf,
    groupOf: groupOf,
    routesRoot: routesRoot,
    applyOps: applyOps,
    fetchCart: fetchCart,
    onThemeCartUpdated: onThemeCartUpdated,
    init: init
  };

  if (root.document) {
    if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', init);
    else init();
  }
})();
